import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { 
  getProducts, 
  getProductTypes, 
  updateStoreStock, 
  getProductCategories, 
  getGameSets, 
  getCardgames,
  getUserProductWishlistIds,
  toggleUserProductWishlist
} from '../../services/supabaseService';
import { ProductType, Product, GameType, isProductPreSale, isTicketProduct } from '../../types';
export { isProductPreSale } from '../../types';
import { useAuth } from '../../components/AuthProvider';
import { useNotification } from '../../components/member/NotificationProvider';
import { useLoginPrompt } from '../../contexts/LoginPromptContext';
import { OfflineWarning } from '../../components/OfflineWarning';
import { supabase } from '../../lib/supabase';
import { LojistaCatalogView } from '../shopkeeper/LojistaCatalogView';

interface ProductsProps {
  onAddToCart: (product: Product) => void;
  activeGame: number | 'All';
  setActiveGame: (game: number | 'All') => void;
}

type SortOption = 'newest' | 'price_asc' | 'price_desc' | 'relevance';

export const Products: React.FC<ProductsProps> = ({ onAddToCart, activeGame, setActiveGame }) => {
  const { isOffline, user } = useAuth();
  const { showNotification } = useNotification();
  const { openLoginPrompt } = useLoginPrompt();
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const urlSlug = routeSlug || searchParams.get('slug') || searchParams.get('p') || searchParams.get('id');

  const isLojista = user?.role_id === 6;
  const [viewAsCustomer, setViewAsCustomer] = useState(false);
  const [selectedSlug, setSelectedSlug] = useState<string | null>(urlSlug || null);
  const [dbGames, setDbGames] = useState<any[]>([]);
  const [dbProductCategories, setDbProductCategories] = useState<any[]>([]);
  const [dbProductTypes, setDbProductTypes] = useState<any[]>([]);
  const [dbSets, setDbSets] = useState<any[]>([]);
  const [categoryFilterId, setCategoryFilterId] = useState<string | number>('all');
  const [typeFilterId, setTypeFilterId] = useState<string | number>('all');
  const [setFilterId, setSetFilterId] = useState<string | number>('all');
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [onlyInStock, setOnlyInStock] = useState(false);
  const [onlyPreSale, setOnlyPreSale] = useState(false);
  const [onlyWishlist, setOnlyWishlist] = useState(false);
  const [wishlistProductIds, setWishlistProductIds] = useState<Set<number>>(new Set());
  const [togglingWishlistId, setTogglingWishlistId] = useState<number | null>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isSubcategoryOpen, setIsSubcategoryOpen] = useState(false);
  const [store, setStore] = useState<any>(null);
  const itemsPerPage = 12;

  // Carrega IDs da lista de desejos de produtos do usuário logado
  useEffect(() => {
    let isMounted = true;
    const loadWishlist = async () => {
      if (!user?.id) {
        setWishlistProductIds(new Set());
        return;
      }
      try {
        const ids = await getUserProductWishlistIds(user.id);
        if (isMounted) {
          setWishlistProductIds(new Set(ids));
        }
      } catch (err) {
        console.error('Erro ao carregar lista de desejos de produtos:', err);
      }
    };
    loadWishlist();
    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  // Alterna produto na lista de desejos
  const handleToggleWishlist = async (e: React.MouseEvent, productId: number | string, productName?: string) => {
    e.stopPropagation();
    e.preventDefault();

    if (!user?.id) {
      openLoginPrompt('Lista de Desejos de Produtos');
      return;
    }

    const pId = Number(productId);
    if (isNaN(pId)) return;

    const wasInWishlist = wishlistProductIds.has(pId);

    // Atualização otimista imediata na interface
    setWishlistProductIds(prev => {
      const next = new Set(prev);
      if (wasInWishlist) {
        next.delete(pId);
      } else {
        next.add(pId);
      }
      return next;
    });
    setTogglingWishlistId(pId);

    const res = await toggleUserProductWishlist(user.id, pId);
    setTogglingWishlistId(null);

    if (res.error) {
      // Reverte em caso de falha
      setWishlistProductIds(prev => {
        const next = new Set(prev);
        if (wasInWishlist) {
          next.add(pId);
        } else {
          next.delete(pId);
        }
        return next;
      });
      showNotification(`Erro ao atualizar lista de desejos: ${res.error}`, 'error');
    } else {
      if (res.inWishlist) {
        showNotification(`${productName || 'Produto'} adicionado à sua Lista de Desejos!`, 'success');
      } else {
        showNotification(`${productName || 'Produto'} removido da Lista de Desejos.`, 'info');
      }
    }
  };

  useEffect(() => {
    if (urlSlug) {
      setSelectedSlug(urlSlug);
    }
  }, [urlSlug]);

  useEffect(() => {
    async function fetchStore() {
        if (isLojista && user?.id) {
            try {
                const { data } = await supabase
                    .from('stores')
                    .select('*, store_members!inner(user_id)')
                    .eq('store_members.user_id', user.id)
                    .maybeSingle();
                setStore(data);
            } catch (e) {
                console.error("Error fetching store:", e);
            }
        }
    }
    fetchStore();
  }, [isLojista, user?.id]);

  const handleAddToStock = async (product: any) => {
    if (!store?.id) {
        showNotification("Loja não encontrada para sua conta.", "error");
        return;
    }

    try {
        const initialPrice = Math.max(0.05, Number(product.price) || 0.05);
        const res = await updateStoreStock(store.id, product.id, 1, initialPrice, false);
        if (res && !(res as any).error) {
            showNotification(`${product.name} adicionado ao seu estoque!`, "success");
        } else {
            throw new Error((res as any)?.error || "Falha ao atualizar estoque");
        }
    } catch (err: any) {
        showNotification(err.message, "error");
    }
  };

  useEffect(() => {
    const unsub = getProducts((data) => {
      setProducts(data);
      setLoading(false);
    });
    
    const fetchMetadata = async () => {
      const [categories, sets, gamesList, typesList] = await Promise.all([
        getProductCategories(),
        getGameSets(),
        getCardgames(),
        getProductTypes()
      ]);
      // Remove a categoria de produto id 3, slug 'ticket', name 'Ingresso' da página de produtos
      const filteredCategories = (categories || []).filter((c: any) => 
        Number(c.id) !== 3 && 
        c.slug?.toLowerCase() !== 'ticket' && 
        c.name?.toLowerCase() !== 'ingresso'
      );
      setDbProductCategories(filteredCategories);
      setDbSets(sets || []);
      setDbGames(gamesList || []);
      setDbProductTypes(typesList || []);
    };
    
    fetchMetadata();
    return () => unsub();
  }, []);

  const filteredSets = useMemo(() => {
    if (activeGame === 'All') {
      return dbSets;
    }
    return dbSets.filter(s => Number(s.game_id) === Number(activeGame));
  }, [dbSets, activeGame]);

  useEffect(() => {
    if (setFilterId !== 'all') {
      const isStillAvailable = filteredSets.some(s => Number(s.id) === Number(setFilterId));
      if (!isStillAvailable) {
        setSetFilterId('all');
      }
    }
  }, [activeGame, filteredSets, setFilterId]);

  // Filtro dinâmico de subcategorias com base no jogo e na categoria selecionada
  const filteredProductTypes = useMemo(() => {
    // Remove tipos de ticket/ingresso da vitrine convencional de produtos
    let types = (dbProductTypes || []).filter(t => {
      const isTicket = 
        Number(t.category_id) === 3 || 
        Number(t.id) === 52 || 
        Number(t.id) === 3 ||
        t.name?.toLowerCase() === 'ticket' || 
        t.name?.toLowerCase() === 'ingresso' || 
        t.name?.toLowerCase() === 'inscrição' ||
        t.name?.toLowerCase() === 'inscricao';
      return !isTicket;
    });

    // Filtra apenas os referentes da categoria selecionada
    if (categoryFilterId !== 'all') {
      const targetCatId = Number(categoryFilterId);
      types = types.filter(t => Number(t.category_id) === targetCatId);
    }

    // Filtra pelo jogo selecionado
    if (activeGame !== 'All') {
      const targetGameId = Number(activeGame);
      types = types.filter(t => t.game_id === null || Number(t.game_id) === targetGameId);
    }

    return types;
  }, [dbProductTypes, activeGame, categoryFilterId]);

  // Se a subcategoria selecionada não existir no novo jogo ou na nova categoria, reseta para 'all'
  useEffect(() => {
    if (typeFilterId !== 'all') {
      const isStillAvailable = filteredProductTypes.some(t => Number(t.id) === Number(typeFilterId));
      if (!isStillAvailable) {
        setTypeFilterId('all');
      }
    }
  }, [activeGame, categoryFilterId, filteredProductTypes, typeFilterId]);

  const mappedProducts = useMemo(() => {
    return products
      .filter(p => {
        if (p.is_available === false) return false;
        // Ingressos nunca devem aparecer na vitrine ou loja de produtos
        if (isTicketProduct(p)) return false;

        return true;
      })
      .map(p => {
        const gameData = Array.isArray(p.games) ? p.games[0] : p.games;
        const typeData = Array.isArray(p.product_types) ? p.product_types[0] : p.product_types;
        
        let stock = 0;
        let price = p.msrp || p.mspr || p.price || 0;

        const stockItems = (Array.isArray(p.store_products) ? p.store_products : [])
          .filter((si: any) => !si.deleted_at && si.store?.sales_enabled !== false);
        stock = stockItems.reduce((acc: number, curr: any) => {
          const available = Math.max(0, (curr.quantity || 0) - (curr.reserved_quantity || 0));
          return acc + available;
        }, 0);
        // If multiple stores have the item, we show the lowest price
        if (stockItems.length > 0) {
          price = Math.min(...stockItems.map((si: any) => si.store_price || p.msrp || p.mspr || 0));
        }
        
        return {
          ...p,
          imageUrl: p.image_url || p.imageUrl || 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400',
          name: p.beauty_name || p.name,
          price,
          stock,
          type: typeData?.name || p.product_type || p.type,
          product_type_id: p.product_type_id ? Number(p.product_type_id) : (typeData?.id ? Number(typeData.id) : null),
          category_id: typeData?.category_id || p.category_id || null,
          game: gameData?.name || p.game || 'TCG',
          game_id: p.game_id ? Number(p.game_id) : (gameData?.id ? Number(gameData.id) : null)
        };
      });
  }, [products]);

  const filteredProducts = useMemo(() => {
    let list = [...mappedProducts];
    
    // Filter by Game (Global focus)
    if (activeGame !== 'All') {
      const targetId = Number(activeGame);
      list = list.filter(p => {
        const gameIdValue = Number(p.game_id);
        return gameIdValue === targetId;
      });
    }
    
    // Filter by Type (Tipo Dinâmico)
    if (typeFilterId !== 'all') {
      const targetTypeId = Number(typeFilterId);
      list = list.filter(p => Number(p.product_type_id) === targetTypeId);
    }

    // Filter by Set (Coleção)
    if (setFilterId !== 'all') {
      const targetSetId = Number(setFilterId);
      list = list.filter(p => {
        return Number(p.game_set_id) === targetSetId || Number(p.set_id) === targetSetId;
      });
    }
    
    // Filter by Category
    if (categoryFilterId !== 'all') {
      list = list.filter(p => Number(p.category_id) === Number(categoryFilterId));
    }

    if (searchQuery) {
      list = list.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    if (onlyInStock) {
      list = list.filter(p => (p.stock || 0) > 0);
    }

    if (onlyPreSale) {
      list = list.filter(p => isProductPreSale(p));
    }

    if (onlyWishlist) {
      list = list.filter(p => wishlistProductIds.has(Number(p.id)));
    }

    return list.sort((a, b) => {
      if (sortBy === 'price_asc') return (a.price || 0) - (b.price || 0);
      if (sortBy === 'price_desc') return (b.price || 0) - (a.price || 0);
      if (sortBy === 'relevance') {
        const sponsoredA = a.is_sponsored ? 1 : 0;
        const sponsoredB = b.is_sponsored ? 1 : 0;
        if (sponsoredA !== sponsoredB) return sponsoredB - sponsoredA;
        const stockA = (a.stock || 0) > 0 ? 1 : 0;
        const stockB = (b.stock || 0) > 0 ? 1 : 0;
        if (stockA !== stockB) return stockB - stockA;
        return (Number(b.id) || 0) - (Number(a.id) || 0);
      }

      // 'newest' (Padrão): Ordenar por data de lançamento mais recente
      const relTimeB = b.release_date ? new Date(b.release_date).getTime() : 0;
      const relTimeA = a.release_date ? new Date(a.release_date).getTime() : 0;
      const validRelB = !isNaN(relTimeB) ? relTimeB : 0;
      const validRelA = !isNaN(relTimeA) ? relTimeA : 0;

      if (validRelB !== validRelA) {
        return validRelB - validRelA;
      }

      // Fallback: created_at mais recente
      const createTimeB = b.created_at ? new Date(b.created_at).getTime() : 0;
      const createTimeA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const validCreateB = !isNaN(createTimeB) ? createTimeB : 0;
      const validCreateA = !isNaN(createTimeA) ? createTimeA : 0;

      if (validCreateB !== validCreateA) {
        return validCreateB - validCreateA;
      }

      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
  }, [mappedProducts, activeGame, typeFilterId, categoryFilterId, setFilterId, searchQuery, sortBy, onlyInStock, onlyPreSale, onlyWishlist, wishlistProductIds]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  
  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProducts, currentPage]);

  const preSaleCount = useMemo(() => {
    return mappedProducts.filter(p => isProductPreSale(p)).length;
  }, [mappedProducts]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeGame, typeFilterId, categoryFilterId, setFilterId, searchQuery, sortBy, onlyInStock, onlyPreSale, onlyWishlist]);

  const selectedProduct = useMemo(() => {
    if (!selectedSlug) return null;
    const decodedSlug = decodeURIComponent(String(selectedSlug));
    const base = products.find(p => 
      !isTicketProduct(p) &&
      (p.slug === decodedSlug || 
      p.slug === selectedSlug || 
      String(p.id) === String(decodedSlug) || 
      String(p.id) === String(selectedSlug))
    );
    if (!base) return null;

    const gameData = Array.isArray(base.games) ? base.games[0] : base.games;
    const typeData = Array.isArray(base.product_types) ? base.product_types[0] : base.product_types;

    return {
      ...base,
      imageUrl: base.image_url || 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=400',
      name: base.beauty_name || base.name,
      game: gameData?.name || 'TCG',
      type: typeData?.name || 'Produto'
    };
  }, [selectedSlug, products]);

  const offers = useMemo(() => {
    if (!selectedProduct) return [];
    const stockItems = (Array.isArray(selectedProduct.store_products) ? selectedProduct.store_products : [])
      .filter((si: any) => !si.deleted_at && si.store?.sales_enabled !== false);
    
    const mapped = stockItems.map((si: any) => {
      const totalStock = si.quantity || 0;
      const reserved = si.reserved_quantity || 0;
      const available = Math.max(0, totalStock - reserved);
      const isOfferPreSale = si.pre_sale === true || si.pre_sale === 'true' || isProductPreSale(selectedProduct);
      return {
        ...selectedProduct, // spread product info first
        id: `${selectedProduct.id}-${si.store?.id}`, // unique id for cart
        productId: selectedProduct.id,
        storeId: si.store?.id,
        storeName: si.store?.name,
        storeLogo: si.store?.logo,
        storeSlug: si.store?.slug,
        isOfficialPartner: si.store?.is_partner,
        stock: available,
        totalStock: totalStock,
        reservedStock: reserved,
        price: si.store_price || selectedProduct.msrp || selectedProduct.mspr || 0,
        type: selectedProduct.type,
        pre_sale: isOfferPreSale,
      };
    });

    return mapped.sort((a: any, b: any) => a.price - b.price);
  }, [selectedProduct]);

  if (isLojista && !viewAsCustomer && !selectedSlug) {
    return (
      <div className="space-y-6">
        {isOffline && <OfflineWarning />}
        <LojistaCatalogView
          store={store}
          onSwitchToMarketplace={() => setViewAsCustomer(true)}
        />
      </div>
    );
  }

  if (selectedSlug) {
    if (loading) {
      return (
        <div className="max-w-4xl mx-auto space-y-8 pb-20 px-4 pt-16 text-center">
          <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">Carregando detalhes do produto...</p>
        </div>
      );
    }

    if (!selectedProduct) {
      return (
        <div className="max-w-4xl mx-auto space-y-8 pb-20 px-4 pt-12 text-center">
          <div className="bg-slate-900/40 border border-slate-800 p-12 rounded-[32px] space-y-4">
            <i className="fas fa-box-open text-slate-600 text-4xl"></i>
            <h3 className="text-lg font-bold text-white uppercase">Produto não encontrado</h3>
            <p className="text-xs text-slate-400">O produto solicitado não foi encontrado ou não está disponível.</p>
            <button
              onClick={() => {
                setSelectedSlug(null);
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/produtos');
                }
              }}
              className="bg-purple-600 hover:bg-purple-500 text-white text-xs font-black uppercase tracking-wider px-6 py-2.5 rounded-xl transition-all inline-flex items-center space-x-2 cursor-pointer"
            >
              <i className="fas fa-arrow-left"></i>
              <span>Voltar</span>
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-4xl mx-auto space-y-6 pb-20 px-3 sm:px-4">
        <button 
          onClick={() => {
            setSelectedSlug(null);
            if (window.history.length > 1) {
              navigate(-1);
            } else {
              navigate('/produtos');
            }
          }} 
          className="group text-slate-400 hover:text-white flex items-center space-x-2 text-xs font-black uppercase tracking-wider transition-colors cursor-pointer pt-2"
        >
          <i className="fas fa-arrow-left transition-transform group-hover:-translate-x-1"></i>
          <span>Voltar para Produtos</span>
        </button>
        
        <div className="bg-slate-900/80 border border-slate-800 rounded-3xl overflow-hidden shadow-xl relative">
          <div className="p-5 sm:p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 sm:gap-10">
            <div className="w-full max-w-[240px] md:w-56 aspect-[3/4] flex-shrink-0 flex items-center justify-center bg-slate-950/40 p-4 rounded-2xl border border-slate-800/60 relative overflow-hidden">

               <img 
                src={selectedProduct.imageUrl} 
                decoding="async"
                className="w-full h-full object-contain" 
                alt={selectedProduct.name} 
               />
            </div>
            <div className="flex-1 text-center md:text-left space-y-3.5 w-full">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="inline-block px-2.5 py-0.5 bg-purple-500/10 border border-purple-500/20 rounded-md text-[10px] font-black text-purple-400 uppercase tracking-widest">
                  {selectedProduct.game}
                </span>

                {/* Botão de Lista de Desejos */}
                <button
                  type="button"
                  onClick={(e) => handleToggleWishlist(e, selectedProduct.id, selectedProduct.name)}
                  disabled={togglingWishlistId === Number(selectedProduct.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border active:scale-95 ${
                    wishlistProductIds.has(Number(selectedProduct.id))
                      ? 'bg-pink-500/15 border-pink-500/40 text-pink-300 hover:bg-pink-500/25 shadow-sm shadow-pink-500/10'
                      : 'bg-slate-800/80 border-slate-700 text-slate-300 hover:text-pink-400 hover:border-pink-500/40'
                  }`}
                  title={wishlistProductIds.has(Number(selectedProduct.id)) ? "Remover da Lista de Desejos" : "Adicionar à Lista de Desejos"}
                >
                  <i className={`${wishlistProductIds.has(Number(selectedProduct.id)) ? 'fas fa-heart text-pink-500' : 'far fa-heart text-slate-400'} text-xs`}></i>
                  <span>{wishlistProductIds.has(Number(selectedProduct.id)) ? 'Na Lista de Desejos' : 'Adicionar aos Desejos'}</span>
                </button>
              </div>

              <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-white uppercase leading-tight">{selectedProduct.name}</h2>
              
              <div className="flex flex-wrap gap-2 justify-center md:justify-start items-center">
                {isProductPreSale(selectedProduct) && (
                  <span className="px-2.5 py-0.5 bg-amber-500/10 border border-amber-500/20 rounded-md text-[9.5px] font-semibold text-amber-400/90 flex items-center gap-1">
                    <i className="fas fa-clock text-[8px] text-amber-400/70"></i>
                    <span>Pré-Venda</span>
                  </span>
                )}
                <span className="px-2.5 py-1 bg-slate-800/80 rounded-lg text-[10px] font-bold text-slate-300 uppercase tracking-wider">
                  {selectedProduct.type}
                </span>
                {selectedProduct.release_date && (
                   <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 bg-slate-800/80 text-slate-400">
                     <i className="far fa-calendar text-[9px]"></i>
                     <span>Lançamento: {new Date(selectedProduct.release_date).toLocaleDateString('pt-BR')}</span>
                   </span>
                )}
                {selectedProduct.msrp && (
                   <span className="px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-lg text-[10px] font-bold text-emerald-400 uppercase tracking-wider">
                     MSRP: R$ {Number(selectedProduct.msrp || selectedProduct.mspr || 0).toFixed(2).replace('.', ',')}
                   </span>
                )}
              </div>

              {isProductPreSale(selectedProduct) && (
                <div className="px-3.5 py-2.5 rounded-xl bg-slate-950/50 border border-slate-800/80 flex items-center gap-2.5 text-left my-2">
                  <i className="fas fa-info-circle text-amber-400/80 text-xs flex-shrink-0"></i>
                  <p className="text-[11px] text-slate-400 leading-normal">
                    Item em pré-venda. O envio ocorre a partir da data de lançamento oficial{selectedProduct.release_date ? ` (${new Date(selectedProduct.release_date).toLocaleDateString('pt-BR')})` : ''}.
                  </p>
                </div>
              )}

              {selectedProduct.description && (
                <p className="text-xs sm:text-sm text-slate-400 leading-relaxed max-w-2xl pt-1">
                  {selectedProduct.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-xs sm:text-sm font-black text-slate-400 uppercase tracking-wider">Ofertas Disponíveis</h3>
            {isLojista && (
                <button 
                  onClick={() => handleAddToStock(selectedProduct)}
                  className="bg-purple-600 hover:bg-purple-500 text-white text-[10px] font-black px-3 py-1.5 rounded-xl transition-colors uppercase tracking-wider shadow-sm"
                >
                  Adicionar ao meu estoque
                </button>
            )}
            <span className="text-[10px] font-bold text-slate-500 uppercase">{offers.length} {offers.length === 1 ? 'vendedor' : 'vendedores'}</span>
          </div>
          <div className="space-y-2.5">
            {offers.length > 0 ? (
              offers.map((offer: any) => (
                <OfferRow 
                  key={offer.id || `offer-${offer.storeId}`} 
                  offer={offer} 
                  onAddToCart={onAddToCart} 
                  isLojista={isLojista} 
                  myStoreId={store?.id}
                />
              ))
            ) : (
                <div className="bg-slate-900/40 border border-slate-800 p-8 sm:p-12 rounded-3xl text-center space-y-2">
                   <i className="fas fa-store-slash text-slate-700 text-2xl sm:text-3xl"></i>
                   <p className="text-xs font-bold text-slate-500 uppercase">Nenhuma loja com estoque disponível no momento.</p>
                </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl 2xl:max-w-[1800px] mx-auto pb-20 px-4 sm:px-6">
      {isOffline && <OfflineWarning />}

      {isLojista && viewAsCustomer && (
        <div className="p-4 sm:p-5 rounded-2xl bg-purple-950/40 border border-purple-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-purple-200 shadow-xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0">
              <i className="fas fa-eye text-base"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-white uppercase tracking-wider">Modo Cliente Ativo (Marketplace Público)</p>
              <p className="text-[11px] text-purple-300/80">Você está navegando como comprador para verificar como a vitrine aparece aos clientes.</p>
            </div>
          </div>
          <button
            onClick={() => setViewAsCustomer(false)}
            className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-black uppercase tracking-wider whitespace-nowrap transition-all shadow-md shadow-purple-600/20 cursor-pointer flex items-center gap-2"
          >
            <i className="fas fa-bolt"></i> Voltar para Adição ao Estoque
          </button>
        </div>
      )}

      {/* MARKETPLACE LAYOUT */}
      <div className="flex flex-col lg:flex-row gap-5">
        
        {/* Mobile Filter Toggle & Quick Info */}
        <div className="lg:hidden flex items-center justify-between bg-slate-900/80 p-3.5 rounded-2xl border border-slate-800">
           <div className="flex items-center gap-2">
             <span className="w-2 h-2 rounded-full bg-purple-500"></span>
             <span className="text-xs font-bold text-white uppercase">{filteredProducts.length} Produtos</span>
             {onlyInStock && (
               <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">Em Estoque</span>
             )}
             {onlyPreSale && (
               <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                 <i className="fas fa-clock text-[7px] text-amber-400"></i>
                 <span>Pré-Venda</span>
               </span>
             )}
             {onlyWishlist && (
               <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-pink-500/10 text-pink-400 border border-pink-500/20">Desejos</span>
             )}
           </div>
           <button 
             type="button"
             onClick={() => setIsFilterOpen(true)}
             className="px-4 py-2 bg-purple-600 hover:bg-purple-500 active:scale-95 text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center space-x-2 transition-colors"
           >
             <i className="fas fa-sliders-h text-xs"></i>
             <span>Filtrar</span>
           </button>
        </div>

        {/* Mobile Drawer Backdrop */}
        {isFilterOpen && (
          <div 
            className="fixed inset-0 bg-black/70 z-50 lg:hidden"
            onClick={() => setIsFilterOpen(false)}
          />
        )}

        {/* Sidebar de Filtros (Desktop + Mobile Drawer) */}
        <aside className={`
          fixed inset-y-0 right-0 z-50 w-full max-w-xs bg-slate-900 p-5 overflow-y-auto border-l border-slate-800 transition-transform duration-200 lg:transition-none
          lg:static lg:inset-auto lg:z-auto lg:w-64 lg:p-0 lg:bg-transparent lg:border-none lg:overflow-visible lg:block lg:flex-shrink-0
          ${isFilterOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
        `}>
          <div className="sticky top-4 bg-slate-900/60 border border-slate-800 rounded-3xl p-5 space-y-5">
            
            {/* Header Mobile para Fechar Filtro */}
            <div className="flex items-center justify-between lg:hidden pb-3 border-b border-slate-800">
              <span className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2">
                <i className="fas fa-sliders text-purple-400 text-xs"></i> Filtros
              </span>
              <button 
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="w-8 h-8 rounded-xl bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center text-sm"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            {/* FILTRO: JOGO */}
            <div className="space-y-2.5">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center space-x-2">
                  <i className="fas fa-gamepad text-purple-500 text-[10px]"></i>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Jogo</label>
                </div>
                {activeGame !== 'All' && (
                  <button
                    onClick={() => setActiveGame('All')}
                    className="text-[10px] font-bold text-purple-400 hover:text-purple-300 uppercase tracking-wider transition-colors cursor-pointer"
                  >
                    Ver Todos
                  </button>
                )}
              </div>
              <div className="relative">
                <select
                  value={activeGame}
                  onChange={(e) => {
                    const val = e.target.value;
                    setActiveGame(val === 'All' ? 'All' : Number(val));
                  }}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-200 focus:outline-none focus:border-purple-500 uppercase tracking-wide cursor-pointer appearance-none transition-colors"
                >
                  <option value="All">Todos os Jogos</option>
                  {dbGames.map(game => (
                    <option key={game.id} value={game.id}>
                      {game.name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 text-xs">
                  <i className="fas fa-chevron-down"></i>
                </div>
              </div>
            </div>

            {/* FILTRO: CATEGORIA (RECOLHÍVEL) */}
            <div className="space-y-2 border-b border-slate-800/60 pb-3">
              <button
                type="button"
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className="w-full flex items-center justify-between px-1 py-1 text-left rounded-lg hover:bg-slate-800/40 transition-colors group cursor-pointer"
              >
                <div className="flex items-center space-x-2">
                  <i className="fas fa-shapes text-purple-500 text-[10px]"></i>
                  <span className="text-[10px] font-black text-slate-400 group-hover:text-slate-200 uppercase tracking-widest transition-colors">
                    Categoria
                  </span>
                  {categoryFilterId !== 'all' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" title="Filtro ativo"></span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {categoryFilterId !== 'all' && (
                    <>
                      <span className="text-[9px] font-bold text-purple-300 max-w-[90px] truncate" title={dbProductCategories.find(c => Number(c.id) === Number(categoryFilterId))?.name}>
                        {dbProductCategories.find(c => Number(c.id) === Number(categoryFilterId))?.name}
                      </span>
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          setCategoryFilterId('all');
                        }}
                        className="text-[9px] font-bold text-purple-400 hover:text-purple-300 uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
                        title="Limpar filtro de categoria"
                      >
                        Limpar
                      </span>
                    </>
                  )}
                  <i className={`fas fa-chevron-down text-[10px] text-slate-500 group-hover:text-slate-300 transition-transform duration-200 ${isCategoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isCategoryOpen && (
                <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1 pt-1 transition-all">
                  <FilterButton 
                    isActive={categoryFilterId === 'all'} 
                    onClick={() => setCategoryFilterId('all')} 
                    label="Todas as Categorias" 
                  />
                  {dbProductCategories.map((cat, idx) => (
                    <FilterButton 
                      key={cat.id || `cat-${idx}`}
                      isActive={categoryFilterId === cat.id}
                      onClick={() => setCategoryFilterId(cat.id)}
                      label={cat.name}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* FILTRO: SUBCATEGORIA (RECOLHÍVEL & DINÂMICO) */}
            <div className="space-y-2 border-b border-slate-800/60 pb-3">
              <button
                type="button"
                onClick={() => setIsSubcategoryOpen(!isSubcategoryOpen)}
                className="w-full flex items-center justify-between px-1 py-1 text-left rounded-lg hover:bg-slate-800/40 transition-colors group cursor-pointer"
              >
                <div className="flex items-center space-x-2">
                  <i className="fas fa-layer-group text-purple-500 text-[10px]"></i>
                  <span className="text-[10px] font-black text-slate-400 group-hover:text-slate-200 uppercase tracking-widest transition-colors">
                    Subcategoria
                  </span>
                  {typeFilterId !== 'all' && (
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" title="Filtro ativo"></span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {typeFilterId !== 'all' && (
                    <>
                      <span className="text-[9px] font-bold text-purple-300 max-w-[90px] truncate" title={filteredProductTypes.find(t => Number(t.id) === Number(typeFilterId))?.name}>
                        {filteredProductTypes.find(t => Number(t.id) === Number(typeFilterId))?.name}
                      </span>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setTypeFilterId('all');
                        }}
                        className="text-[9px] font-bold text-purple-400 hover:text-purple-300 uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/10 hover:bg-purple-500/20 transition-colors"
                        title="Limpar filtro de subcategoria"
                      >
                        Limpar
                      </span>
                    </>
                  )}
                  <i className={`fas fa-chevron-down text-[10px] text-slate-500 group-hover:text-slate-300 transition-transform duration-200 ${isSubcategoryOpen ? 'rotate-180' : ''}`}></i>
                </div>
              </button>

              {isSubcategoryOpen && (
                <div className="space-y-1 max-h-[220px] overflow-y-auto pr-1 pt-1 transition-all">
                  <FilterButton 
                    isActive={typeFilterId === 'all'} 
                    onClick={() => setTypeFilterId('all')} 
                    label="Todas as Subcategorias" 
                  />
                  {filteredProductTypes.length === 0 ? (
                    <p className="text-[11px] text-slate-500 italic px-2 py-1">
                      Nenhuma subcategoria disponível.
                    </p>
                  ) : (
                    filteredProductTypes.map((type, idx) => (
                      <FilterButton 
                        key={type.id || `type-${idx}`}
                        isActive={Number(typeFilterId) === Number(type.id)}
                        onClick={() => setTypeFilterId(type.id)}
                        label={type.name}
                      />
                    ))
                  )}
                </div>
              )}
            </div>

            {/* FILTRO: DISPONIBILIDADE (ESTOQUE) */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 px-1">
                <i className="fas fa-boxes-stacked text-purple-500 text-[10px]"></i>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Estoque</label>
              </div>
              <button
                type="button"
                onClick={() => setOnlyInStock(!onlyInStock)}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-colors border flex items-center justify-between cursor-pointer ${
                  onlyInStock 
                    ? 'bg-purple-600/15 border-purple-500 text-purple-400 font-extrabold' 
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                }`}
              >
                <span>Apenas Com Estoque</span>
                <div className={`w-7 h-4 rounded-full transition-colors relative ${onlyInStock ? 'bg-purple-600' : 'bg-slate-800'}`}>
                  <div className={`w-3 h-3 rounded-full bg-white absolute top-0.5 transition-transform ${onlyInStock ? 'right-0.5' : 'left-0.5'}`} />
                </div>
              </button>
            </div>

            {/* FILTRO: PRÉ-VENDA */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 px-1">
                <i className="fas fa-clock text-amber-500 text-[10px]"></i>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Lançamentos</label>
              </div>
              <button
                type="button"
                onClick={() => setOnlyPreSale(!onlyPreSale)}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-colors border flex items-center justify-between cursor-pointer ${
                  onlyPreSale 
                    ? 'bg-amber-500/15 border-amber-500 text-amber-400 font-extrabold shadow-sm shadow-amber-500/10' 
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <span>Apenas Pré-Venda</span>
                  {preSaleCount > 0 ? (
                    <span className="px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 text-[8px] font-bold">
                      {preSaleCount}
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.2 rounded bg-amber-500/20 text-amber-300 text-[8px] font-bold">
                      PRE
                    </span>
                  )}
                </span>
                <div className={`w-7 h-4 rounded-full transition-colors relative ${onlyPreSale ? 'bg-amber-600' : 'bg-slate-800'}`}>
                  <div className={`w-3 h-3 rounded-full bg-white absolute top-0.5 transition-transform ${onlyPreSale ? 'right-0.5' : 'left-0.5'}`} />
                </div>
              </button>
            </div>

            {/* FILTRO: LISTA DE DESEJOS */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 px-1">
                <i className="fas fa-heart text-pink-500 text-[10px]"></i>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Lista de Desejos</label>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!user?.id && !onlyWishlist) {
                    openLoginPrompt('Lista de Desejos de Produtos');
                    return;
                  }
                  setOnlyWishlist(!onlyWishlist);
                }}
                className={`w-full text-left px-3.5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-colors border flex items-center justify-between cursor-pointer ${
                  onlyWishlist 
                    ? 'bg-pink-500/15 border-pink-500 text-pink-400 font-extrabold' 
                    : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <span>Apenas Meus Desejos</span>
                  {wishlistProductIds.size > 0 && (
                    <span className="px-1.5 py-0.2 rounded-full bg-pink-500/20 text-pink-300 text-[9px] font-bold">
                      {wishlistProductIds.size}
                    </span>
                  )}
                </span>
                <div className={`w-7 h-4 rounded-full transition-colors relative ${onlyWishlist ? 'bg-pink-600' : 'bg-slate-800'}`}>
                  <div className={`w-3 h-3 rounded-full bg-white absolute top-0.5 transition-transform ${onlyWishlist ? 'right-0.5' : 'left-0.5'}`} />
                </div>
              </button>
            </div>

            {/* FILTRO: ORDENAÇÃO */}
            <div className="space-y-3">
              <div className="flex items-center space-x-2 px-1">
                <i className="fas fa-sort-amount-down text-emerald-500 text-[10px]"></i>
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Classificar</label>
              </div>
              <div className="grid grid-cols-1 gap-1">
                {[
                  { id: 'newest', label: 'Lançamentos Recentes (Padrão)' },
                  { id: 'relevance', label: 'Em Destaque' },
                  { id: 'price_asc', label: 'Preço: Menor' },
                  { id: 'price_desc', label: 'Preço: Maior' }
                ].map(opt => (
                  <button 
                    key={opt.id}
                    type="button"
                    onClick={() => setSortBy(opt.id as SortOption)}
                    className={`text-left px-3.5 py-2.5 rounded-xl text-[11px] font-bold transition-colors border ${
                      sortBy === opt.id ? 'bg-slate-800 border-emerald-500/50 text-emerald-400' : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Botão de Fechar no Mobile */}
            <div className="lg:hidden pt-3 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-black uppercase tracking-wider"
              >
                Aplicar e Ver ({filteredProducts.length})
              </button>
            </div>

          </div>
        </aside>

        {/* Grid de Itens */}
        <div className="flex-1 space-y-5">
           <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 group">
                 <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 text-sm"></i>
                 <input 
                   type="text" 
                   placeholder="Procure por booster box, decks, sleeves..."
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-11 pr-4 py-3 text-xs sm:text-sm font-medium focus:outline-none focus:border-purple-500 transition-colors placeholder:text-slate-500"
                 />
                 {searchQuery && (
                   <button
                     onClick={() => setSearchQuery('')}
                     className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white text-xs"
                   >
                     <i className="fas fa-times"></i>
                   </button>
                 )}
              </div>

              <div className="hidden sm:flex items-center px-4 bg-slate-900/60 border border-slate-800 rounded-xl">
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider whitespace-nowrap">
                   {filteredProducts.length} <span className="text-slate-600">produtos</span>
                 </span>
              </div>
           </div>

           {/* Barra de Filtros Ativos */}
           {(activeGame !== 'All' || typeFilterId !== 'all' || categoryFilterId !== 'all' || setFilterId !== 'all' || onlyInStock || onlyPreSale || onlyWishlist || searchQuery || sortBy !== 'newest') && (
             <div className="flex items-center gap-1.5 flex-wrap text-xs">
               <span className="text-[10px] font-black uppercase text-slate-500 tracking-wider">Filtros:</span>
               {activeGame !== 'All' && (
                 <button
                   onClick={() => setActiveGame('All')}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-600/15 border border-purple-500/30 text-purple-300 text-[11px] font-bold hover:bg-purple-600/25 transition-colors cursor-pointer"
                 >
                   <span>Jogo: {dbGames.find(g => Number(g.id) === Number(activeGame))?.name || activeGame}</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {typeFilterId !== 'all' && (
                 <button
                   onClick={() => setTypeFilterId('all')}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-600/15 border border-purple-500/30 text-purple-300 text-[11px] font-bold hover:bg-purple-600/25 transition-colors cursor-pointer"
                 >
                   <span>Subcategoria: {filteredProductTypes.find(t => Number(t.id) === Number(typeFilterId))?.name || typeFilterId}</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {categoryFilterId !== 'all' && (
                 <button
                   onClick={() => setCategoryFilterId('all')}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-600/15 border border-purple-500/30 text-purple-300 text-[11px] font-bold hover:bg-purple-600/25 transition-colors cursor-pointer"
                 >
                   <span>Categoria: {dbProductCategories.find(c => Number(c.id) === Number(categoryFilterId))?.name || categoryFilterId}</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {onlyInStock && (
                 <button
                   onClick={() => setOnlyInStock(false)}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-600/15 border border-purple-500/30 text-purple-300 text-[11px] font-bold hover:bg-purple-600/25 transition-colors cursor-pointer"
                 >
                   <span>Com Estoque</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {onlyPreSale && (
                 <button
                   onClick={() => setOnlyPreSale(false)}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[11px] font-bold hover:bg-amber-500/25 transition-colors cursor-pointer"
                 >
                   <i className="fas fa-clock text-[9px] text-amber-400"></i>
                   <span>Apenas Pré-Venda</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {onlyWishlist && (
                 <button
                   onClick={() => setOnlyWishlist(false)}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-pink-600/15 border border-pink-500/30 text-pink-300 text-[11px] font-bold hover:bg-pink-600/25 transition-colors cursor-pointer"
                 >
                   <i className="fas fa-heart text-[9px] text-pink-400"></i>
                   <span>Meus Desejos ({wishlistProductIds.size})</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {searchQuery && (
                 <button
                   onClick={() => setSearchQuery('')}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-purple-600/15 border border-purple-500/30 text-purple-300 text-[11px] font-bold hover:bg-purple-600/25 transition-colors cursor-pointer"
                 >
                   <span>Busca: "{searchQuery}"</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               {sortBy !== 'newest' && (
                 <button
                   onClick={() => setSortBy('newest')}
                   className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-600/15 border border-emerald-500/30 text-emerald-300 text-[11px] font-bold hover:bg-emerald-600/25 transition-colors cursor-pointer"
                 >
                   <span>Ordenação: {sortBy === 'price_asc' ? 'Preço: Menor' : sortBy === 'price_desc' ? 'Preço: Maior' : 'Em Destaque'}</span>
                   <i className="fas fa-times text-[9px]"></i>
                 </button>
               )}
               <button
                 onClick={() => {
                   setActiveGame('All');
                   setTypeFilterId('all');
                   setCategoryFilterId('all');
                   setSetFilterId('all');
                   setOnlyInStock(false);
                   setOnlyPreSale(false);
                   setOnlyWishlist(false);
                   setSearchQuery('');
                   setSortBy('newest');
                 }}
                 className="text-[10px] font-black uppercase text-slate-500 hover:text-white transition-colors underline cursor-pointer ml-1"
               >
                 Limpar todos
               </button>
             </div>
           )}

           {/* Grid Otimizado para Mobile e Desktop (Zero layout shifting, GPU ultra leve) */}
           <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-5">
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-[3/4] bg-slate-900/60 border border-slate-800 rounded-2xl animate-pulse"></div>
                ))
              ) : (
                paginatedItems.map((product) => {
                  const target = product.slug || product.id;
                  const hasStock = (product.stock || 0) > 0;
                  const isWishlisted = wishlistProductIds.has(Number(product.id));
                  const isPreSale = isProductPreSale(product);

                  return (
                    <div 
                      key={product.id || `prod-${product.slug}`} 
                      onClick={() => {
                        setSelectedSlug(target);
                        navigate(`/produtos/${target}`);
                      }} 
                      className="group bg-slate-900/70 border border-slate-800 hover:border-slate-700/80 rounded-2xl overflow-hidden transition-all cursor-pointer flex flex-col relative"
                    >
                      {/* Image container */}
                      <div className="aspect-square p-3 sm:p-4 bg-slate-950/40 relative flex items-center justify-center overflow-hidden">
                        <img 
                          src={product.imageUrl} 
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-contain transition-transform duration-300 ease-out group-hover:scale-105" 
                          alt={product.name} 
                        />
                        
                  
                        {/* Botão de Lista de Desejos */}
                        <button
                          type="button"
                          onClick={(e) => handleToggleWishlist(e, product.id, product.name)}
                          disabled={togglingWishlistId === Number(product.id)}
                          className={`absolute top-2 right-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md backdrop-blur-md z-10 active:scale-90 ${
                            isWishlisted 
                              ? 'bg-pink-500/20 border border-pink-500/50 text-pink-400 hover:bg-pink-500/30 hover:scale-110 shadow-pink-500/20' 
                              : 'bg-slate-900/80 border border-slate-700/60 text-slate-400 hover:text-pink-400 hover:border-pink-500/40 hover:bg-slate-900 hover:scale-110'
                          }`}
                          title={isWishlisted ? "Remover da Lista de Desejos" : "Adicionar à Lista de Desejos"}
                        >
                          <i className={`${isWishlisted ? 'fas fa-heart text-pink-500' : 'far fa-heart'} text-xs sm:text-sm`}></i>
                        </button>
                      </div>

                      {/* Info container */}
                      <div className="p-3 sm:p-4 flex-1 flex flex-col justify-between space-y-2 sm:space-y-3">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-[8px] sm:text-[9px] font-bold text-slate-500 uppercase tracking-wider">
                            <span className="truncate max-w-[55%]">{product.game || 'TCG'}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-slate-600 truncate">{product.type}</span>
                            </div>
                          </div>
                          <h4 
                            className="text-[11px] sm:text-xs font-bold text-white uppercase line-clamp-2 h-8 sm:h-9 group-hover:text-purple-400 transition-colors leading-tight" 
                            title={product.name}
                          >
                            {product.name}
                          </h4>
                          {isPreSale && (

                            <div className="px-1.5 py-0.5 rounded-md text-[8px] font-medium text-amber-400/90 ">
                              <i className="fas fa-clock text-[7px] text-amber-400/70"></i>
                              <span>Pré-Venda</span>
                            </div>
                        )}

                          {isPreSale && product.release_date && (
                            <div className="text-[8.5px] font-medium text-slate-400 flex items-center gap-1 pt-0.5">
                              <i className="far fa-calendar text-[8px] text-slate-500"></i>
                              <span>Lançamento: {new Date(product.release_date).toLocaleDateString('pt-BR')}</span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-slate-800/60">
                          <div className="flex flex-col">
                            <span className="text-[8px] font-bold uppercase leading-none text-slate-500">
                              {isPreSale ? 'Reserva a partir de' : 'A partir de'}
                            </span>
                            <span className="text-xs sm:text-sm font-black mt-0.5 text-emerald-400">
                              R$ {(product.price || 0).toFixed(2).replace('.', ',')}
                            </span>
                          </div>
                          <button 
                            type="button"
                            onClick={(e) => {
                              if (isLojista) {
                                e.stopPropagation();
                                handleAddToStock(product);
                              }
                            }}
                            title={isLojista ? "Adicionar ao estoque da loja" : isPreSale ? "Garantir na Pré-Venda" : "Ver detalhes"}
                            className="w-8 h-8 rounded-xl flex items-center justify-center transition-all active:scale-90 bg-slate-800/80 hover:bg-purple-600 hover:text-white text-slate-400"
                          >
                            <i className={`fas ${isLojista ? 'fa-box-archive' : isPreSale ? 'fa-clock' : 'fa-arrow-right'} text-[10px]`}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {!loading && paginatedItems.length === 0 && (
                <div className="col-span-full py-20 text-center space-y-4">
                  <div className={`w-16 h-16 ${onlyWishlist ? 'bg-pink-500/10 border-pink-500/30 text-pink-400' : 'bg-slate-900 border-slate-800 text-slate-600'} border rounded-2xl flex items-center justify-center mx-auto text-2xl`}>
                    <i className={`fas ${onlyWishlist ? 'fa-heart' : 'fa-box-open'}`}></i>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-black text-white uppercase tracking-wider">
                      {onlyWishlist ? 'Sua Lista de Desejos de Produtos está vazia' : 'Nenhum produto encontrado'}
                    </p>
                    <p className="text-xs text-slate-500 max-w-md mx-auto">
                      {onlyWishlist 
                        ? 'Clique no ícone de coração nos produtos da vitrine para favoritá-los e acompanhar aqui.' 
                        : 'Tente ajustar seus filtros ou busca textual.'}
                    </p>
                  </div>
                  <button 
                    onClick={() => { setActiveGame('All'); setTypeFilterId('all'); setCategoryFilterId('all'); setSetFilterId('all'); setSearchQuery(''); setOnlyInStock(false); setOnlyPreSale(false); setOnlyWishlist(false); setSortBy('newest'); }}
                    className="px-5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-black uppercase transition-colors cursor-pointer"
                  >
                    {onlyWishlist ? 'Ver Todos os Produtos' : 'Resetar Filtros'}
                  </button>
                </div>
              )}
           </div>

           {/* Paginação Mobile e Desktop responsiva */}
           {totalPages > 1 && (
             <div className="flex items-center justify-between sm:justify-center gap-2 pt-8">
               <button
                 disabled={currentPage === 1}
                 onClick={() => { setCurrentPage(prev => Math.max(1, prev - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                 className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2 text-slate-400 hover:border-purple-500 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold"
               >
                 <i className="fas fa-chevron-left text-[10px]"></i>
                 <span className="hidden sm:inline">Anterior</span>
               </button>
               
               <div className="flex items-center space-x-1">
                 {Array.from({ length: totalPages }, (_, i) => i + 1)
                   .filter(page => {
                     return page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1;
                   })
                   .map((page, index, array) => {
                     const isFirst = index === 0;
                     const prevPage = array[index - 1];
                     const showEllipsis = !isFirst && page - prevPage > 1;

                     return (
                       <React.Fragment key={page}>
                         {showEllipsis && <span className="text-slate-600 font-bold px-1 text-xs">...</span>}
                         <button
                           onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                           className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl border font-black text-xs transition-colors ${
                             currentPage === page 
                               ? 'bg-purple-600 border-purple-500 text-white' 
                               : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-white'
                           }`}
                         >
                           {page}
                         </button>
                       </React.Fragment>
                     );
                   })}
               </div>

               <button
                 disabled={currentPage === totalPages}
                 onClick={() => { setCurrentPage(prev => Math.min(totalPages, prev + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                 className="px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-2 text-slate-400 hover:border-purple-500 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold"
               >
                 <span className="hidden sm:inline">Próxima</span>
                 <i className="fas fa-chevron-right text-[10px]"></i>
               </button>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

const FilterButton: React.FC<{ isActive: boolean; onClick: () => void; label: string; colorClass?: string }> = ({ isActive, onClick, label, colorClass = 'bg-purple-600 border-purple-500' }) => (
  <button 
    type="button"
    onClick={onClick}
    className={`w-full text-left px-3 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-colors border ${
      isActive ? `${colorClass} text-white` : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700 hover:text-slate-200'
    }`}
  >
    {label}
  </button>
);

const OfferRow: React.FC<{ offer: Product; onAddToCart: (p: Product) => void; isLojista: boolean; myStoreId?: number | string }> = ({ offer, onAddToCart, isLojista, myStoreId }) => {
  const isOwnOffer = isLojista && myStoreId && Number(offer.storeId) === Number(myStoreId);

  return (
    <div 
      className="bg-slate-900/70 border border-slate-800 p-4 sm:p-5 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-colors hover:border-slate-700"
    >
      <div className="flex items-center space-x-3.5 w-full sm:w-auto">
        {offer.storeId ? (
          <Link to={`/loja/${offer.storeId}`} className="w-12 h-12 rounded-xl overflow-hidden border border-slate-700 bg-slate-950 p-0.5 hover:border-purple-500 transition-colors flex-shrink-0">
             <img src={offer.storeLogo || `https://ui-avatars.com/api/?name=${offer.storeName || 'Store'}`} className="w-full h-full object-cover rounded-lg" alt={offer.storeName} loading="lazy" />
          </Link>
        ) : (
          <div className="w-12 h-12 rounded-xl overflow-hidden border border-slate-700 bg-slate-950 p-0.5 flex-shrink-0">
             <img src={offer.storeLogo || `https://ui-avatars.com/api/?name=${offer.storeName || 'Store'}`} className="w-full h-full object-cover rounded-lg" alt={offer.storeName} loading="lazy" />
          </div>
        )}
        <div className="space-y-0.5 min-w-0 flex-1">
          <div className="flex flex-col">
            {offer.storeId ? (
              <Link to={`/loja/${offer.storeId}`} className="hover:text-purple-400 text-white transition-colors truncate">
                <h4 className="font-bold text-sm uppercase tracking-tight truncate">{offer.storeName || 'Lojista Cardumy'}</h4>
              </Link>
            ) : (
              <h4 className="font-bold text-white text-sm uppercase tracking-tight truncate">{offer.storeName || 'Lojista Cardumy'}</h4>
            )}
            {offer.storeId && (
              <Link to={`/loja/${offer.storeId}`} className="inline-flex items-center space-x-1 text-[9px] font-black text-purple-400 uppercase tracking-wider hover:text-purple-300 transition-colors">
                <span>Ver Loja</span>
                <i className="fas fa-chevron-right text-[7px]"></i>
              </Link>
            )}
          </div>
          <div className="flex items-center space-x-2 mt-0.5 flex-wrap">
            <span className={`text-[9px] font-black uppercase tracking-wider ${offer.stock && offer.stock > 0 ? 'text-emerald-400' : 'text-slate-500'}`}>
              {offer.stock || 0} disponíveis
            </span>
            {offer.reservedStock && offer.reservedStock > 0 ? (
              <span className="text-amber-400 font-bold uppercase text-[9px] bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.2 rounded flex items-center space-x-1">
                <i className="fas fa-clock text-[7px]"></i>
                <span>{offer.reservedStock} res.</span>
              </span>
            ) : null}
            {offer.pre_sale && (
              <span className="text-amber-400/90 font-medium uppercase text-[8.5px] bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded-md flex items-center gap-1">
                <i className="fas fa-clock text-[7px] text-amber-400/70"></i>
                <span>Pré-Venda</span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center w-full sm:w-auto justify-between sm:justify-end border-t sm:border-t-0 border-slate-800/60 pt-3 sm:pt-0 gap-4">
        <div className="flex flex-col sm:items-end">
          <span className="text-[9px] font-bold text-slate-500 uppercase leading-none mb-0.5">Preço Unitário</span>
          <span className="text-xl sm:text-2xl font-black text-emerald-400">R$ {(offer.price || 0).toFixed(2).replace('.', ',')}</span>
        </div>
        {(!isLojista || isOwnOffer) && (
          <button 
            type="button"
            onClick={() => onAddToCart({ ...offer, quantity: 1 } as any)}
            disabled={offer.stock === 0}
            className={`px-6 sm:px-8 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition-all active:scale-95 ${
              offer.stock === 0 
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed' 
              : isOwnOffer
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-purple-600 hover:bg-purple-500 text-white'
            }`}
          >
            {offer.stock === 0 ? 'Esgotado' : isOwnOffer ? 'Venda Balcão' : offer.pre_sale ? 'Reservar' : (offer.type === 'Ingresso' ? 'Garantir' : 'Adicionar')}
          </button>
        )}
      </div>
    </div>
  );
};
